import { useContext, useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import emailjs from '@emailjs/browser';
import "./ContactSupport.css";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebook,
  FaPaperPlane,
} from "react-icons/fa";
import { CanvasContext } from "../../contexts/CanvasContext";

const ContactSupport = () => {
  const { isDarkTheme } = useContext(CanvasContext);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  // Add form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_email: 'princegondaliya20@gmail.com',
      };

      // Replace these with your actual EmailJS credentials
      await emailjs.send(
        'YOUR_SERVICE_ID', // Create this in EmailJS dashboard
        'YOUR_TEMPLATE_ID', // Create this in EmailJS dashboard
        templateParams,
        'YOUR_PUBLIC_KEY' // Get this from EmailJS dashboard
      );

      setStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      id="contact"
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
      className={`contact-support-container ${isDarkTheme ? "dark-theme" : ""}`}
    >
      <motion.div className="contact-support-header" variants={itemVariants}>
        <h2 className={`contact-support-title ${isDarkTheme ? "dark-theme" : ""}`}>
          Let's Connect
        </h2>
        <p className={`contact-support-subtitle ${isDarkTheme ? "dark-theme" : ""}`}>
          Have a project in mind? We'd love to hear about it. Let's discuss how we can help bring your ideas to life.
        </p>
      </motion.div>

      <div className="contact-support-content">
        <motion.div
          className={`contact-info-card ${isDarkTheme ? "dark-theme" : ""}`}
          variants={itemVariants}
        >
          <div className="contact-info-header">
            <h3>Get in Touch</h3>
            <p>We're here to help and answer any questions you might have</p>
          </div>
          
          <ul className="contact-info-list">
            <li>
              <div className="contact-icon-wrapper">
                <FaEnvelope className="icon" />
              </div>
              <div className="contact-details">
                <h4>Email Us</h4>
                <span>princegondaliya20@gmail.com</span>
              </div>
            </li>
            <li>
              <div className="contact-icon-wrapper">
                <FaMapMarkerAlt className="icon" />
              </div>
              <div className="contact-details">
                <h4>Visit Us</h4>
                <span>Silver Trade Center, Surat, Gujarat, India 394105</span>
              </div>
            </li>
            <li>
              <div className="contact-icon-wrapper">
                <FaFacebook className="icon" />
              </div>
              <div className="contact-details">
                <h4>Follow Us</h4>
                <span>NexGen Sites - Company</span>
              </div>
            </li>
          </ul>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className={`contact-form-card ${isDarkTheme ? "dark-theme" : ""}`}
          variants={itemVariants}
        >
          <div className="form-header">
            <h3>Send us a Message</h3>
            <p>Fill out the form below and we'll get back to you shortly</p>
          </div>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input 
                type="text" 
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                required 
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required 
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="subject">Subject</label>
              <input 
                type="text" 
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="What's this about?"
                required 
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="message">Message</label>
              <textarea 
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project..."
                rows="4"
                required
              ></textarea>
            </div>
          </div>

          {status && (
            <div className={`form-status ${status}`}>
              {status === 'success' 
                ? 'Message sent successfully!' 
                : 'Failed to send message. Please try again.'}
            </div>
          )}

          <button
            type="submit"
            className={`send-button ${isDarkTheme ? "dark-theme" : ""}`}
            disabled={loading}
          >
            <span>{loading ? 'Sending...' : 'Send Message'}</span>
            <FaPaperPlane className="send-icon" />
          </button>
        </motion.form>
      </div>

      <motion.section
        className={`map-section ${isDarkTheme ? "dark-theme" : ""}`}
        variants={itemVariants}
      >
        <div className="map-header">
          <h2>Our Location</h2>
          <p>Come visit us at our office</p>
        </div>
        <div className="map-container">
          <iframe
            title="STC ,Surat Gujarat"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2852.8516723659927!2d72.86806076829943!3d21.232887638669023!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f7adffdd9f9%3A0x8e5947b84d098fd9!2sSilver%20Trade%20Center!5e0!3m2!1sen!2sin!4v1723528272020!5m2!1sen!2sin"
            width="100%"
            height="450"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </motion.section>
    </motion.div>
  );
};

export default ContactSupport;
